// ---------------------------------------------------------------------------
// useAISearch.ts
//
// Responsible ONLY for making the POST /api/chat request.
// Turn state, Firestore persistence, and optimistic UI are all owned by
// useConversation. ConversationView orchestrates the two hooks.
//
// WHAT THIS HOOK DOES
// -------------------
//   - Builds the history[] array from turns (text extraction)
//   - Posts to POST /api/chat
//   - Returns { submit, loading, error }
//
// WHAT THIS HOOK DOES NOT DO
// --------------------------
//   - Own turns[] state
//   - Write to Firestore
//   - Manage conversationId
// ---------------------------------------------------------------------------

import { useState, useCallback } from 'react';
import { apiPost, ApiError } from './apiClient';
import type { ConversationBlock, ConversationTurn, AISource } from './aiSearchTypes';
import type { ContextRef } from './contextTypes';

// ---------------------------------------------------------------------------
// API types (internal to this file)
// ---------------------------------------------------------------------------

interface ChatRequestHistoryEntry {
    role: 'user' | 'assistant';
    content: string;
}

interface ChatRequest {
    conversation_id: string;
    query: string;
    history: ChatRequestHistoryEntry[];
    /** Present on the first turn only — the item the user was viewing. */
    conversation_context_ref?: ContextRef | null;
    /** Present on any turn that has user-attached @/$/# references. */
    mention_refs?: ContextRef[];
}

interface ChatResponse {
    conversation_id: string;
    title?: string | null;
    blocks: ConversationBlock[];
    sources: AISource[];
}

// ---------------------------------------------------------------------------
// History builder
//
// Flattens the turn list into alternating user/assistant pairs for the backend.
// System turns are excluded — they are context, not conversation history.
// Assistant content is plain text from TextBlocks only; rich blocks are not
// summarised because the backend uses this for LLM context, not for rendering.
// ---------------------------------------------------------------------------

function extractTextContent(turn: ConversationTurn): string {
    // Prefer the pre-extracted `text` field (set when loaded from Firestore)
    if (turn.text) return turn.text;
    return turn.blocks
        .filter((b): b is { type: 'text'; content: string } => b.type === 'text')
        .map((b) => b.content)
        .join('\n\n');
}

function buildHistory(turns: ConversationTurn[]): ChatRequestHistoryEntry[] {
    return turns
        .filter((t) => t.role !== 'system')
        .map((t) => ({
            role: t.role as 'user' | 'assistant',
            content: extractTextContent(t),
        }));
}

// ---------------------------------------------------------------------------
// Submit params
// ---------------------------------------------------------------------------

export interface SubmitParams {
    conversationId: string;
    query: string;
    /** Current turn list — used to build the history[] sent to the backend. */
    turns: ConversationTurn[];
    /** Sent on the first turn only. */
    conversationContextRef?: ContextRef | null;
    /** Sent on any turn that includes user mentions. */
    mentionRefs?: ContextRef[];
}

// ---------------------------------------------------------------------------
// Return shape
// ---------------------------------------------------------------------------

export interface AISearchResult {
    blocks: ConversationBlock[];
    sources: AISource[];
    /** Title suggestion returned by the backend on the first turn only. */
    title?: string | null;
}

export interface UseAISearchReturn {
    submit: (params: SubmitParams) => Promise<AISearchResult>;
    loading: boolean;
    error: string | null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAISearch(): UseAISearchReturn {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = useCallback(async (params: SubmitParams): Promise<AISearchResult> => {
        const {
            conversationId,
            query,
            turns,
            conversationContextRef,
            mentionRefs,
        } = params;

        setError(null);
        setLoading(true);

        try {
            const body: ChatRequest = {
                conversation_id: conversationId,
                query,
                history: buildHistory(turns),
                ...(conversationContextRef !== undefined
                    ? { conversation_context_ref: conversationContextRef }
                    : {}),
                ...(mentionRefs?.length ? { mention_refs: mentionRefs } : {}),
            };

            const data = await apiPost<ChatResponse>('/api/chat', body);

            return {
                blocks: data.blocks,
                sources: data.sources,
                title: data.title,
            };
        } catch (err) {
            const message =
                err instanceof ApiError
                    ? err.message
                    : 'Something went wrong. Please try again.';
            setError(message);
            // Re-throw so ConversationView can append an error turn
            throw new Error(message);
        } finally {
            setLoading(false);
        }
    }, []);

    return { submit, loading, error };
}